import { api } from '@/api'
import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { RestrictedResourceModel, RestrictedWorker } from './models'

export const useRestrictedResourcesList = () => {
  return useQuery({
    queryKey: ['RESTRICTED_RESOURCE_LIST'],
    queryFn: () => api.get(`/restricted-resource/all`) as Promise<RestrictedResourceModel[]>,
    staleTime: 50_000,
    placeholderData: keepPreviousData
  })
}

export const useUnconfirmedWorkersForRR = (resourceIds: string[]) => {
  return useQueries({
    queries: resourceIds
      ? resourceIds.map((resourceId) => {
          return {
            queryKey: ['RESTRICTED_WORKERS_LIST', resourceId],
            queryFn: () => api.get<RestrictedWorker[]>(`/restricted-resource/${resourceId}/workers`),
            select: (data: RestrictedWorker[]) => data.some((item) => !item.confirmed && item.type === 'extension')
          }
        })
      : [] // if users is undefined, an empty array will be returned
  })
}

export const useRestrictedWorkersList = (resourceId: string, hasAccess: boolean) => {
  return useQuery({
    queryKey: ['RESTRICTED_WORKERS_LIST', resourceId],
    queryFn: () => api.get<RestrictedWorker[]>(`/restricted-resource/${resourceId}/workers`),
    enabled: hasAccess,
    staleTime: 50_000
  })
}

export const useGrantAccessToRestrictedResource = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (resourceId: string) => api.post(`/restricted-resource/${resourceId}/grant`),
    onSuccess: (_, resourceId) => {
      queryClient.refetchQueries({ queryKey: ['RESTRICTED_RESOURCE_LIST'] })
      queryClient.invalidateQueries({ queryKey: ['RESTRICTED_WORKERS_LIST', resourceId] })
    }
  })
}

export const useRevokeAccessToRestrictedResource = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (resourceId: string) => api.post(`/restricted-resource/${resourceId}/revoke`),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['RESTRICTED_RESOURCE_LIST'] })
    }
  })
}

export const useConfirmWorkerAuthentication = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ resourceId, workerId }: { resourceId: string; workerId: string }) =>
      api.post(`restricted-resource/${resourceId}/${workerId}/confirm`),
    onSuccess: (_, variables) => {
      queryClient.refetchQueries({ queryKey: ['RESTRICTED_WORKERS_LIST', variables.resourceId] })
    }
  })
}
